/**
 * AgentHealth — admin screen for the email/workflow agent (Phase C).
 *
 * Consolidates the agent controls that used to live in the legacy index.html
 * "Agent" + "Telegram Bot" panels into the React app:
 *   • master + per-feature switches (agent_enabled + the Part C gates)
 *   • per-job model map (bot / extraction / personality) + the locked PDF lane
 *   • usage (daily LLM calls) + admin chat
 *   • recent agent_runs (the A/B model viewer) + open alerts
 *
 * All controls read/write app_settings; everything is admin-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';

// Per-job model options (mirror Constants.gs PART_C_MODEL_DEFAULTS choices).
const MODEL_OPTIONS = [
  { v: 'deepseek|deepseek-chat',           label: 'DeepSeek V4 (cheapest)' },
  { v: 'claude|claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
  { v: 'claude|claude-sonnet-4-6',         label: 'Claude Sonnet 4.6' },
  { v: 'claude|claude-opus-4-8',           label: 'Claude Opus 4.8' },
];
const MODEL_DEFAULTS = {
  bot: 'deepseek|deepseek-chat',
  extraction: 'deepseek|deepseek-chat',
  personality: 'claude|claude-sonnet-4-6',
};

export default function AgentHealth({ profile, navigate } = {}) {
  const isAdmin = profile?.role === 'admin';
  const sb = getSupabase();
  const [settings, setSettings] = useState({});
  const [runs, setRuns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [pending, setPending] = useState([]);
  const [runners, setRunners] = useState([]);
  const [ovCompany, setOvCompany] = useState('');
  const [ovRunner, setOvRunner] = useState('');
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sb) { setStatus('error'); setError(new Error('Supabase not available')); return; }
    setStatus('loading');
    const [setRes, runRes, alRes, pendRes, runnerRes] = await Promise.all([
      sb.from('app_settings').select('key, value'),
      sb.from('agent_runs').select('*').order('ts', { ascending: false }).limit(20),
      sb.from('agent_alerts').select('*').eq('resolved', false).order('ts', { ascending: false }).limit(20),
      sb.from('pending_emails').select('*').eq('resolved', false).order('ts', { ascending: false }).limit(20),
      sb.from('runners').select('id, data').order('id'),
    ]);
    if (setRes.error) { setStatus('error'); setError(setRes.error); return; }
    const s = {}; (setRes.data || []).forEach((r) => { s[r.key] = r.value; });
    setSettings(s);
    setRuns(runRes.data || []);
    setAlerts(alRes.data || []);
    setPending(pendRes.data || []);
    setRunners((runnerRes.data || []).filter((r) => r.data?.active !== false));
    setStatus('ready');
  }, [sb]);

  useEffect(() => { load(); }, [load]);

  const saveSetting = async (key, value) => {
    if (!isAdmin) return;
    const { error: e } = await sb.from('app_settings').upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    if (e) { setError(e); return; }
    await load();
  };

  const boolSetting = (key, dflt = false) => {
    const v = settings[key];
    return v === undefined || v === null ? dflt : v === true;
  };

  const setModel = async (job, v) => {
    const [provider, model] = v.split('|');
    const map = (settings.agent_model_map && typeof settings.agent_model_map === 'object') ? { ...settings.agent_model_map } : {};
    map[job] = { provider, model };
    await saveSetting('agent_model_map', map);
  };
  const modelValue = (job) => {
    const m = settings.agent_model_map && settings.agent_model_map[job];
    return m ? `${m.provider}|${m.model}` : MODEL_DEFAULTS[job];
  };

  const dropPending = async (id) => {
    if (!window.confirm('Drop this pending email? It will not be retried.')) return;
    const { error: e } = await sb.from('pending_emails').update({ resolved: true }).eq('id', id);
    if (e) { setError(e); return; }
    await load();
  };

  // Read-modify-write against the live row (not local state) — the agent's
  // Telegram self-learn flow writes this same key between page loads.
  const mutateOverrides = async (fn) => {
    const { data } = await sb.from('app_settings').select('value').eq('key', 'mercury_company_overrides').maybeSingle();
    const overrides = (data && data.value) || {};
    fn(overrides);
    await saveSetting('mercury_company_overrides', overrides);
  };
  const addOverride = async () => {
    const co = ovCompany.trim().toLowerCase();
    if (!co || !ovRunner) return;
    await mutateOverrides((o) => { o[co] = ovRunner; });
    setOvCompany('');
  };
  const removeOverride = async (co) => {
    await mutateOverrides((o) => { delete o[co]; });
  };

  // Telegram admin tools (ported from the legacy index.html Telegram panel).
  // The agent's /exec endpoint config lives in app_settings.agent_poll_now.
  const [tgMsg, setTgMsg] = useState(null);
  const tgCall = async (params) => {
    const cfg = settings.agent_poll_now;
    if (!cfg?.url || !cfg?.token) { setTgMsg('Agent endpoint not configured (app_settings.agent_poll_now)'); return null; }
    try {
      const u = new URL(cfg.url);
      u.searchParams.set('token', cfg.token);
      Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, String(v)));
      const r = await fetch(u.toString());
      const j = await r.json();
      if (!j.ok) { setTgMsg('Failed: ' + (j.error || 'unknown')); return null; }
      return j;
    } catch (e) { setTgMsg('Error: ' + (e?.message || e)); return null; }
  };
  const tgPump = async () => {
    setTgMsg('Pulling…');
    const j = await tgCall({ action: 'tg-resync' });
    if (j) {
      const n = j.consumed_updates || 0;
      setTgMsg(n ? `Processed ${n} pending update${n === 1 ? '' : 's'} ✓` : 'No pending updates');
      await load();
    }
  };
  const tgTest = async (chatId) => {
    setTgMsg('Sending…');
    const j = await tgCall({ action: 'tg-test', chat_id: chatId, text: '🧪 Test from the FinOps admin UI — your Telegram link is working.' });
    if (j) setTgMsg('Test message sent ✓');
  };
  const tgManualLink = async (runner) => {
    const name = runner.data?.name || runner.id;
    const cid = window.prompt(`Enter ${name}'s Telegram chat_id:\n\n(They can find it by DMing @userinfobot on Telegram.)`);
    if (!cid) return;
    const n = parseInt(cid.trim(), 10);
    if (!n || Number.isNaN(n)) { setTgMsg('Not a valid chat_id'); return; }
    const { error: e } = await sb.from('runners').update({ data: { ...runner.data, telegram_chat_id: n } }).eq('id', runner.id);
    if (e) { setTgMsg('Failed: ' + e.message); return; }
    setTgMsg(`${name} linked (chat_id ${n}) ✓`);
    await load();
  };
  const tgUnlink = async (runner) => {
    const name = runner.data?.name || runner.id;
    if (!window.confirm(`Unlink ${name}'s Telegram? They'll need to re-do /me ${String(name).toLowerCase()} to relink.`)) return;
    const next = { ...runner.data };
    delete next.telegram_chat_id;
    const { error: e } = await sb.from('runners').update({ data: next }).eq('id', runner.id);
    if (e) { setTgMsg('Failed: ' + e.message); return; }
    setTgMsg(`${name} unlinked`);
    await load();
  };

  const Toggle = ({ label, desc, k, dflt }) => (
    <div className="es-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)' }}>{desc}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className={'btn btn-sm ' + (boolSetting(k, dflt) ? 'btn-primary' : 'btn-ghost')} onClick={() => saveSetting(k, true)}>On</button>
        <button className={'btn btn-sm ' + (!boolSetting(k, dflt) ? 'btn-danger' : 'btn-ghost')} onClick={() => saveSetting(k, false)}>Off</button>
      </div>
    </div>
  );

  const ModelRow = ({ job, label, desc }) => (
    <div className="es-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {desc && <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)' }}>{desc}</div>}
      </div>
      <select className="btn btn-ghost btn-sm" value={modelValue(job)} onChange={(e) => setModel(job, e.target.value)}>
        {MODEL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );

  if (!isAdmin) {
    return <div className="app"><main className="page"><div className="card">Admins only.</div></main></div>;
  }
  if (status === 'loading') {
    return <div className="app"><main className="page"><div className="card" style={{ color: 'var(--text-2)' }}>Loading agent health…</div></main></div>;
  }
  if (status === 'error') {
    return <div className="app"><main className="page"><div className="card" style={{ color: 'var(--rose, #dc2626)' }}>Couldn't load: {String(error?.message || error)}</div></main></div>;
  }

  const calls = settings.llm_calls_today || { count: 0 };

  return (
    <div className="app">
      <main className="page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h1 style={{ margin: 0 }}>Agent Health</h1>
          {navigate && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate('home')}>← Back</button>}
        </header>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Switches</h2>
          <Toggle label="Agent enabled" desc="Master kill switch — also /pause /resume on Telegram" k="agent_enabled" dflt={true} />
          <Toggle label="Detector" desc="Auto-draft money movements from broker emails" k="pc_detector_enabled" dflt={false} />
          <Toggle label="Q&A bot" desc="Investors can ask the Telegram bot questions" k="pc_bot_enabled" dflt={false} />
          <Toggle label="LUPE personality" desc="Reminders in LUPE's voice (needs an approved daily hook)" k="pc_engagement_enabled" dflt={false} />
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Models (per job)</h2>
          <ModelRow job="bot" label="Bot model" desc="Conversational Q&A assistant" />
          <ModelRow job="extraction" label="Extraction model" desc="Email field extraction (non-PDF lane)" />
          <ModelRow job="personality" label="Personality model" desc="LUPE reminder voice" />
          <div className="es-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>PDF / BGS extraction</div>
              <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)' }}>Locked to a PDF-capable Claude model — DeepSeek silently drops PDFs</div>
            </div>
            <span className="mono" style={{ color: 'var(--text-3, #94a3b8)' }}>Claude (auto)</span>
          </div>
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Usage</h2>
          <div className="es-row" style={{ display: 'flex', padding: '6px 0' }}>
            <span style={{ flex: 1 }}>LLM calls today</span>
            <span className="mono">{calls.count || 0} / 100</span>
          </div>
          <div className="es-row" style={{ display: 'flex', padding: '6px 0' }}>
            <span style={{ flex: 1 }}>Telegram admin chat</span>
            <span className="mono">{settings.admin_chat_id || '—'}</span>
          </div>
          <div className="es-row" style={{ display: 'flex', padding: '6px 0' }}>
            <span style={{ flex: 1 }}>LLM provider (legacy default)</span>
            <span className="mono">{String(settings.llm_provider || 'claude')}</span>
          </div>
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Recent runs ({runs.length})</h2>
          {runs.length === 0 ? <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No runs yet.</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ fontSize: 12, width: '100%' }}>
                <thead><tr><th>When</th><th>Type</th><th>Model</th><th>OK</th><th>$</th></tr></thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.ts).toLocaleString()}</td>
                      <td>{r.email_type || ''}</td>
                      <td className="mono">{String(r.model || '').replace('claude-haiku-4-5-20251001', 'haiku').replace('deepseek-chat', 'deepseek')}</td>
                      <td style={{ color: r.success ? 'var(--green, #059669)' : 'var(--rose, #dc2626)' }}>{r.success ? '✓' : '✗'}</td>
                      <td style={{ textAlign: 'right' }}>{r.cost_estimate_usd ? '$' + Number(r.cost_estimate_usd).toFixed(4) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 style={{ margin: 0, flex: 1 }}>Telegram links</h2>
            <button className="btn btn-ghost btn-sm" onClick={tgPump}>⟳ Pull pending updates</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)', margin: '6px 0 8px' }}>
            Members link by DMing <span className="mono">/start</span> then <span className="mono">/me &lt;name&gt;</span> to the bot. Manual link is the fallback (paste their chat_id from @userinfobot).
          </div>
          {tgMsg && <div style={{ fontSize: 12, marginBottom: 8 }}>{tgMsg}</div>}
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: 12, width: '100%' }}>
              <thead><tr><th>Name</th><th>Chat</th><th>Status</th><th style={{ textAlign: 'right' }}></th></tr></thead>
              <tbody>
                {runners.map((r) => {
                  const cid = r.data?.telegram_chat_id || null;
                  const isAdminChat = cid && String(cid) === String(settings.admin_chat_id || '');
                  return (
                    <tr key={r.id}>
                      <td>{r.data?.name || r.id}</td>
                      <td className="mono" style={{ color: 'var(--text-3)' }}>{cid ? String(cid) : '—'}</td>
                      <td>{cid ? (isAdminChat ? 'admin chat' : 'linked') : <span style={{ color: '#b45309' }}>not linked</span>}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {cid ? (
                          <>
                            <button className="btn btn-ghost btn-sm" onClick={() => tgTest(cid)}>Test</button>{' '}
                            <button className="btn btn-danger btn-sm" onClick={() => tgUnlink(r)}>Unlink</button>
                          </>
                        ) : (
                          <button className="btn btn-ghost btn-sm" onClick={() => tgManualLink(r)}>Link manually</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Pending emails ({pending.length})</h2>
          <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)', marginBottom: 8 }}>
            Gold emails the agent couldn't match yet — it retries them automatically until the give-up date. Drop removes one from the queue.
          </div>
          {pending.length === 0 ? <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No pending emails.</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ fontSize: 12, width: '100%' }}>
                <thead><tr><th>Queued</th><th>Type</th><th>Retries</th><th>Give up</th><th>Key field</th><th></th></tr></thead>
                <tbody>
                  {pending.map((p) => {
                    const d = p.extracted_data || {};
                    const keyField = d.costcoOrderNumber || d.poNumber || d.trackingNumber || d.amount || '';
                    return (
                      <tr key={p.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(p.ts).toLocaleString()}</td>
                        <td>{p.email_type}</td>
                        <td style={{ textAlign: 'center' }}>{p.retry_count || 0}</td>
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--text-3)' }}>{p.give_up_at ? new Date(p.give_up_at).toLocaleDateString() : ''}</td>
                        <td className="mono">{String(keyField)}</td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => dropPending(p.id)}>Drop</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Mercury company → runner overrides</h2>
          <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)', marginBottom: 8 }}>
            Built-in: RNA Clarity → Mahmoud · Stepfound → Kholy · Genocraft → Khaled. Overrides below cover new companies; the agent also adds entries itself when you answer its Telegram prompts.
          </div>
          {Object.keys(settings.mercury_company_overrides || {}).length > 0 && (
            <table className="data-table" style={{ fontSize: 12, width: '100%', marginBottom: 8 }}>
              <thead><tr><th>Company (lowercase)</th><th>Runner</th><th></th></tr></thead>
              <tbody>
                {Object.entries(settings.mercury_company_overrides || {}).map(([co, rid]) => (
                  <tr key={co}>
                    <td className="mono">{co}</td>
                    <td>{runners.find((r) => r.id === rid)?.data?.name || rid}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => removeOverride(co)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={ovCompany}
              onChange={(e) => setOvCompany(e.target.value)}
              placeholder="Company name (e.g. acme corp)"
              style={{ flex: 1, minWidth: 160, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border2, #e2e8f0)', background: 'transparent', color: 'inherit', font: 'inherit', fontSize: 13 }}
            />
            <select className="btn btn-ghost btn-sm" value={ovRunner} onChange={(e) => setOvRunner(e.target.value)}>
              <option value="">Runner…</option>
              {runners.map((r) => <option key={r.id} value={r.id}>{r.data?.name || r.id}</option>)}
            </select>
            <button className="btn btn-primary btn-sm" onClick={addOverride} disabled={!ovCompany.trim() || !ovRunner}>+ Add</button>
          </div>
        </section>

        <section className="card">
          <h2 style={{ marginTop: 0 }}>Open alerts ({alerts.length})</h2>
          {alerts.length === 0 ? <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No open alerts.</div> : alerts.map((a) => (
            <div key={a.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border2, #eee)', fontSize: 13 }}>
              <span className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--text-3)' }}>{new Date(a.ts).toLocaleDateString()}</span>
              <span style={{ flex: 1 }}>{a.message || a.category}</span>
              <button className="btn btn-ghost btn-sm" onClick={async () => { await sb.from('agent_alerts').update({ resolved: true, resolved_at: new Date().toISOString() }).eq('id', a.id); await load(); }}>Dismiss</button>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
